import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  getDevices,
  createDevice,
  updateDevice,
  deleteDevice,
  getAllEvents,
  getMealTypes,
} from "../services/api";
import { useApiQuery, useApiMutation } from "../hooks/useApiQuery";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowPathIcon,
  XMarkIcon,
  DevicePhoneMobileIcon,
  CakeIcon,
} from "@heroicons/react/24/outline";
import { useSocket } from "../context/SocketContext";

const DevicesManagement = () => {
  const socket = useSocket();
  const [showModal, setShowModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);

  const [formData, setFormData] = useState({
    deviceId: "",
    name: "",
    eventId: "",
    status: "active",
    handlesMeals: false,
    mealTypeIds: [],
  });

  const {
    data: devices = [],
    isLoading: devicesLoading,
    isError: devicesError,
    refetch: refetchDevices,
  } = useApiQuery(["devices"], () => getDevices().then((res) => res.data), {
    staleTime: 5 * 60 * 1000,
  });

  const { data: events = [], isLoading: eventsLoading } = useApiQuery(
    ["all-events"],
    () => getAllEvents().then((res) => res.data),
    { staleTime: 5 * 60 * 1000 },
  );

  // Fetch meal types for the currently selected event
  const { data: mealTypes = [] } = useApiQuery(
    ["meal-types", formData.eventId],
    () => getMealTypes(formData.eventId).then((r) => r.data),
    { enabled: Boolean(formData.eventId), staleTime: 5 * 60 * 1000 },
  );

  const isLoading = devicesLoading || eventsLoading;

  const createMutation = useApiMutation(
    (data) => createDevice(data).then((res) => res.data),
    {
      onSuccess: () => {
        handleCloseModal();
        refetchDevices();
      },
      onError: (e) =>
        alert(e.response?.data?.message || "Error creating device"),
    },
  );

  const updateMutation = useApiMutation(
    ({ id, data }) => updateDevice(id, data).then((res) => res.data),
    {
      onSuccess: () => {
        handleCloseModal();
        refetchDevices();
      },
      onError: (e) =>
        alert(e.response?.data?.message || "Error updating device"),
    },
  );

  const deleteMutation = useApiMutation(
    (id) => deleteDevice(id).then((res) => res.data),
    { onSuccess: () => refetchDevices() },
  );

  useEffect(() => {
    if (!socket) return;
    const refresh = () => refetchDevices();
    socket.on("deviceCreated", refresh);
    socket.on("deviceUpdated", refresh);
    socket.on("deviceDeleted", refresh);
    return () => {
      socket.off("deviceCreated", refresh);
      socket.off("deviceUpdated", refresh);
      socket.off("deviceDeleted", refresh);
    };
  }, [socket, refetchDevices]);

  const emptyForm = () => ({
    deviceId: "",
    name: "",
    eventId: "",
    status: "active",
    handlesMeals: false,
    mealTypeIds: [],
  });

  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setEditingDevice(null);
    setFormData(emptyForm());
  }, []);

  const handleEdit = useCallback((device) => {
    const existingMeals = Array.isArray(device?.meals) ? device.meals : [];
    setEditingDevice(device);
    setFormData({
      deviceId: device.deviceId,
      name: device.name,
      eventId: device.eventId?._id || device.eventId || "",
      status: device.status,
      handlesMeals: existingMeals.length > 0,
      mealTypeIds: existingMeals.map((m) => String(m.mealTypeId)),
    });
    setShowModal(true);
  }, []);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (formData.handlesMeals && formData.mealTypeIds.length === 0) {
        alert("Select at least one meal or turn off meal handling");
        return;
      }

      const payload = {
        deviceId: formData.deviceId.trim(),
        name: formData.name.trim(),
        eventId: formData.eventId,
        status: formData.status,
        mealTypeIds: formData.handlesMeals ? formData.mealTypeIds : [],
      };

      if (editingDevice) {
        updateMutation.mutate({ id: editingDevice._id, data: payload });
      } else {
        createMutation.mutate(payload);
      }
    },
    [editingDevice, formData, createMutation, updateMutation],
  );

  const handleDelete = useCallback(
    (id) => {
      if (window.confirm("Delete this device?")) {
        deleteMutation.mutate(id);
      }
    },
    [deleteMutation],
  );

  const toggleMeal = (mealId) => {
    setFormData((prev) => {
      const sid = String(mealId);
      const exists = prev.mealTypeIds.includes(sid);
      return {
        ...prev,
        mealTypeIds: exists
          ? prev.mealTypeIds.filter((id) => id !== sid)
          : [...prev.mealTypeIds, sid],
      };
    });
  };

  const stats = useMemo(() => {
    return {
      totalDevices: devices.length,
      activeDevices: devices.filter((d) => d.status === "active").length,
    };
  }, [devices]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3" />
          <p className="text-gray-500 text-sm">Loading devices...</p>
        </div>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;
  const isDeleting = deleteMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Devices Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage all scanning devices across your events
          </p>
        </div>
        <button
          onClick={() => {
            setFormData(emptyForm());
            setEditingDevice(null);
            setShowModal(true);
          }}
          disabled={isSubmitting}
          className="inline-flex items-center gap-2 bg-[#1672b9] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50"
        >
          <PlusIcon className="w-4 h-4" />
          Add Device
        </button>
      </div>

      {devicesError && (
        <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-lg text-sm">
          Failed to load devices. Please refresh the page.
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="p-3 bg-[#1672b9]/10 rounded-lg">
            <DevicePhoneMobileIcon className="w-6 h-6 text-[#1672b9]" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total Devices</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.totalDevices}
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
          <div className="p-3 bg-green-50 rounded-lg">
            <DevicePhoneMobileIcon className="w-6 h-6 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Active Devices</p>
            <p className="text-2xl font-semibold text-gray-900">
              {stats.activeDevices}
            </p>
          </div>
        </div>
      </div>

      {/* Devices table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-medium text-gray-900">All Devices</h2>
          <button
            onClick={() => refetchDevices()}
            className="text-gray-400 hover:text-[#1672b9] p-1"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Device ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Event
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Meals
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {devices.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-500">
                    No devices found.
                  </td>
                </tr>
              ) : (
                devices.map((device) => {
                  const deviceMeals = Array.isArray(device.meals)
                    ? device.meals
                    : [];
                  return (
                    <tr key={device._id} className="hover:bg-gray-50/50">
                      <td className="px-6 py-4 text-sm font-mono text-gray-900">
                        {device.deviceId}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {device.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {device.eventId?.name || "—"}
                      </td>
                      <td className="px-6 py-4">
                        {deviceMeals.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {deviceMeals.map((m) => (
                              <span
                                key={m.mealTypeId}
                                className="inline-flex items-center gap-1 bg-[#1672b9]/5 text-[#1672b9] text-xs font-medium px-2 py-0.5 rounded-full"
                              >
                                <CakeIcon className="w-3 h-3" />
                                {m.mealTypeName}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">
                            Not a meal device
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            device.status === "active"
                              ? "bg-[#1672b9] text-white"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {device.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleEdit(device)}
                          disabled={isSubmitting || isDeleting}
                          className="text-gray-400 hover:text-[#1672b9] mr-4 disabled:opacity-50"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(device._id)}
                          disabled={isDeleting || isSubmitting}
                          className="text-gray-400 hover:text-red-500 disabled:opacity-50"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full my-8">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900">
                {editingDevice ? "Edit Device" : "Add New Device"}
              </h3>
              <button
                onClick={handleCloseModal}
                disabled={isSubmitting}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device ID
                </label>
                <input
                  type="text"
                  value={formData.deviceId}
                  onChange={(e) =>
                    setFormData({ ...formData, deviceId: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Device Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assigned Event
                </label>
                <select
                  value={formData.eventId}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      eventId: e.target.value,
                      mealTypeIds: [],
                    })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                  required
                  disabled={isSubmitting}
                >
                  <option value="">Select Event</option>
                  {events.map((event) => (
                    <option key={event._id} value={event._id}>
                      {event.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1672b9]/20 focus:border-[#1672b9]"
                  disabled={isSubmitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              {/* Meal toggle */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        formData.handlesMeals
                          ? "bg-[#1672b9]/10"
                          : "bg-gray-100"
                      }`}
                    >
                      <CakeIcon
                        className={`w-5 h-5 ${
                          formData.handlesMeals
                            ? "text-[#1672b9]"
                            : "text-gray-400"
                        }`}
                      />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        Meal device
                      </p>
                      <p className="text-xs text-gray-500">
                        Scans will try to redeem selected meals
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setFormData((p) => ({
                        ...p,
                        handlesMeals: !p.handlesMeals,
                        mealTypeIds: !p.handlesMeals ? p.mealTypeIds : [],
                      }))
                    }
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      formData.handlesMeals ? "bg-[#1672b9]" : "bg-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                        formData.handlesMeals
                          ? "translate-x-6"
                          : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>

                {formData.handlesMeals && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {!formData.eventId ? (
                      <p className="text-xs text-amber-600">
                        Select an event first
                      </p>
                    ) : mealTypes.length === 0 ? (
                      <p className="text-xs text-amber-600">
                        No meals configured for this event
                      </p>
                    ) : (
                      <div className="space-y-1.5 max-h-48 overflow-y-auto">
                        {mealTypes.map((m) => {
                          const isChecked = formData.mealTypeIds.includes(
                            String(m._id),
                          );
                          return (
                            <label
                              key={m._id}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer ${
                                isChecked
                                  ? "bg-[#1672b9]/5 border-[#1672b9]"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleMeal(m._id)}
                                className="w-4 h-4 accent-[#1672b9]"
                              />
                              <span className="text-sm text-gray-900 flex-1">
                                {m.name}
                              </span>
                              {m.price > 0 && (
                                <span className="text-xs text-gray-500">
                                  ₹{m.price}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                  className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-[#1672b9] text-white rounded-lg text-sm font-medium hover:bg-[#0f5a9e] disabled:opacity-50"
                >
                  {isSubmitting
                    ? "Saving..."
                    : editingDevice
                      ? "Update Device"
                      : "Create Device"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicesManagement;
